import BackendService from 'BackendService';
import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Contact from '../../../models/Contact';
import { AdminChatAssistResult } from '../../../models/admin/AdminChatAssistResult';
import { VeraPromptProposal } from '../../../models/admin';
import {
  clearAdminAssistDraftStorage,
  ensureAdminAssistPreviewSynced,
  persistAdminAssistTranscript,
  saveAdminAssistCoachingSandbox,
  saveAdminAssistIntentSandbox,
} from './adminAssistSandboxApi';
import { createTranscriptMessage } from './adminAssistSandboxLoaders';
import {
  AdminAssistDraft,
  PreviewAssistTranscriptMessage,
} from './veraAdminChatAssistPromptSandboxUtils';

export interface AdminAssistPromptSnapshot {
  markdown: string;
  productionMarkdown: string;
  intentText: string;
  intentProductionText: string;
  groundingText: string;
  groundingProductionText: string;
  intentTextRef: MutableRefObject<string>;
  groundingTextRef: MutableRefObject<string>;
  lastSubmittedRef: MutableRefObject<string>;
  intentLastSubmittedRef: MutableRefObject<string | null>;
  groundingLastSubmittedRef: MutableRefObject<string | null>;
  userHasEditedRef: MutableRefObject<boolean>;
  setMarkdown: (value: string) => void;
  setEditorContent: (value: string) => void;
  setIsDirty: (value: boolean) => void;
  setIntentText: (value: string) => void;
  setIntentIsDirty: (value: boolean) => void;
  setGroundingText: (value: string) => void;
  setGroundingIsDirty: (value: boolean) => void;
  setPromptSynced: (value: boolean) => void;
  setIntentSynced: (value: boolean) => void;
  setGroundingSynced: (value: boolean) => void;
  setSavedDraft: (draft: AdminAssistDraft | null) => void;
  syncPromptNow: (value: string) => void;
  syncIntentNow: (value: string) => void;
  syncGroundingNow: (value: string) => void;
  scheduleDraftSave: (
    nextMarkdown: string,
    nextIntent: string,
    nextGrounding: string,
    contact: Contact | null,
    messages: PreviewAssistTranscriptMessage[],
  ) => void;
  clearAssistResult: () => void;
}

interface UseAdminAssistSandboxWorkflowOptions {
  sessionIdRef: MutableRefObject<string>;
  prompts: AdminAssistPromptSnapshot;
}

export function useAdminAssistSandboxWorkflow({
  sessionIdRef,
  prompts,
}: UseAdminAssistSandboxWorkflowOptions) {
  const [contactSearch, setContactSearch] = useState('');
  const [contactOptions, setContactOptions] = useState<Contact[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const [transcript, setTranscript] = useState<PreviewAssistTranscriptMessage[]>([]);
  const [investorComposeText, setInvestorComposeText] = useState('');
  const [adminComposeText, setAdminComposeText] = useState('');

  const [assistLoading, setAssistLoading] = useState(false);
  const [assistResult, setAssistResult] = useState<AdminChatAssistResult | null>(null);
  const [assistError, setAssistError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedProposal, setSubmittedProposal] = useState<VeraPromptProposal | null>(null);

  useEffect(() => {
    if (contactSearch.length < 2) {
      setContactOptions([]);
      return;
    }
    setContactLoading(true);
    new BackendService()
      .searchContact(contactSearch, true)
      .then((results: Contact[]) => setContactOptions(results))
      .catch(() => setContactOptions([]))
      .finally(() => setContactLoading(false));
  }, [contactSearch]);

  const addMessage = (role: 'investor' | 'admin', rawText: string) => {
    const text = rawText.trim();
    if (!text || !selectedContact) return;
    prompts.userHasEditedRef.current = true;
    const message = createTranscriptMessage(role, text, selectedContact);
    const next = [...transcript, message];
    setTranscript(next);
    if (role === 'investor') setInvestorComposeText('');
    else setAdminComposeText('');
    prompts.clearAssistResult();
    persistAdminAssistTranscript(sessionIdRef.current, next);
    prompts.scheduleDraftSave(
      prompts.markdown,
      prompts.intentTextRef.current,
      prompts.groundingTextRef.current,
      selectedContact,
      next,
    );
  };

  const resetSandbox = () => {
    sessionIdRef.current = uuidv4();
    setTranscript([]);
    prompts.clearAssistResult();
    setAssistError(null);
    prompts.setPromptSynced(true);
    prompts.setIntentSynced(true);
    prompts.setGroundingSynced(true);
  };

  const runAssist = async () => {
    if (!selectedContact?.id) return;
    setAssistLoading(true);
    try {
      prompts.setPromptSynced(false);
      prompts.setIntentSynced(false);
      prompts.setGroundingSynced(false);
      await ensureAdminAssistPreviewSynced(
        sessionIdRef.current,
        prompts.markdown,
        prompts.intentTextRef.current,
        prompts.groundingTextRef.current,
      );
      prompts.setPromptSynced(true);
      prompts.setIntentSynced(true);
      prompts.setGroundingSynced(true);
      if (transcript.length > 0) {
        await new BackendService().saveVeraAdminAssistPreviewTranscript(
          sessionIdRef.current,
          transcript,
        );
      }
    } catch {
      setAssistLoading(false);
      return;
    }
    setAssistError(null);
    new BackendService()
      .previewVeraAdminAssist(selectedContact.id, sessionIdRef.current)
      .then((result) => setAssistResult(result))
      .catch((err) => {
        setAssistError(err?.message || err?.error || 'Unable to run Vera assist preview.');
        prompts.clearAssistResult();
      })
      .finally(() => setAssistLoading(false));
  };

  const submitProposal = () => {
    setSaving(true);
    setError(null);
    setSubmittedProposal(null);
    new BackendService()
      .submitVeraAdminAssistProposal(
        prompts.markdown,
        sessionIdRef.current,
        undefined,
        prompts.intentText || prompts.intentProductionText,
        prompts.groundingText || prompts.groundingProductionText,
      )
      .then((proposal) => {
        setSubmittedProposal(proposal);
        prompts.lastSubmittedRef.current = prompts.markdown;
        prompts.intentLastSubmittedRef.current = prompts.intentText;
        prompts.groundingLastSubmittedRef.current = prompts.groundingText;
        prompts.setIsDirty(false);
        prompts.setIntentIsDirty(false);
        prompts.setGroundingIsDirty(false);
        clearAdminAssistDraftStorage();
        prompts.setSavedDraft(null);
      })
      .catch((err) => setError(err?.message || err?.error || 'Failed to submit proposal.'))
      .finally(() => setSaving(false));
  };

  const dismissDraft = () => {
    clearAdminAssistDraftStorage();
    prompts.setSavedDraft(null);
    setSubmittedProposal(null);
    setError(null);
    prompts.clearAssistResult();
    setAssistError(null);

    if (prompts.productionMarkdown) {
      prompts.setMarkdown(prompts.productionMarkdown);
      prompts.setEditorContent(prompts.productionMarkdown);
      prompts.setIsDirty(false);
      prompts.lastSubmittedRef.current = prompts.productionMarkdown;
      prompts.syncPromptNow(prompts.productionMarkdown);
      saveAdminAssistCoachingSandbox(prompts.productionMarkdown);
    }
    if (prompts.intentProductionText) {
      prompts.intentTextRef.current = prompts.intentProductionText;
      prompts.setIntentText(prompts.intentProductionText);
      prompts.setIntentIsDirty(false);
      prompts.intentLastSubmittedRef.current = prompts.intentProductionText;
      prompts.syncIntentNow(prompts.intentProductionText);
      saveAdminAssistIntentSandbox(prompts.intentProductionText);
    }
    if (prompts.groundingProductionText) {
      prompts.groundingTextRef.current = prompts.groundingProductionText;
      prompts.setGroundingText(prompts.groundingProductionText);
      prompts.setGroundingIsDirty(false);
      prompts.groundingLastSubmittedRef.current = prompts.groundingProductionText;
      prompts.syncGroundingNow(prompts.groundingProductionText);
    }

    prompts.userHasEditedRef.current = false;
    sessionIdRef.current = uuidv4();
    setTranscript([]);
    persistAdminAssistTranscript(sessionIdRef.current, []);
  };

  const restoreDraft = (draftToRestore: AdminAssistDraft) => {
    prompts.userHasEditedRef.current = true;
    prompts.setMarkdown(draftToRestore.markdown);
    prompts.setEditorContent(draftToRestore.markdown);
    prompts.syncPromptNow(draftToRestore.markdown);
    if (draftToRestore.intentText !== undefined) {
      prompts.intentTextRef.current = draftToRestore.intentText;
      prompts.setIntentText(draftToRestore.intentText);
      prompts.setIntentIsDirty(true);
      prompts.syncIntentNow(draftToRestore.intentText);
      saveAdminAssistIntentSandbox(draftToRestore.intentText);
    }
    if (draftToRestore.groundingText !== undefined) {
      prompts.groundingTextRef.current = draftToRestore.groundingText;
      prompts.setGroundingText(draftToRestore.groundingText);
      prompts.setGroundingIsDirty(true);
      prompts.syncGroundingNow(draftToRestore.groundingText);
    }
    setTranscript(draftToRestore.transcript ?? []);
    persistAdminAssistTranscript(sessionIdRef.current, draftToRestore.transcript ?? []);
    prompts.setSavedDraft(null);
    clearAdminAssistDraftStorage();
  };

  const handleContactChange = (contact: Contact | null) => {
    setSelectedContact(contact);
    prompts.scheduleDraftSave(
      prompts.markdown,
      prompts.intentTextRef.current,
      prompts.groundingTextRef.current,
      contact,
      transcript,
    );
  };

  const clearAssistResult = useCallback(() => setAssistResult(null), []);

  return {
    contactSearch,
    setContactSearch,
    contactOptions,
    contactLoading,
    selectedContact,
    setSelectedContact: handleContactChange,
    transcript,
    setTranscript,
    investorComposeText,
    setInvestorComposeText,
    adminComposeText,
    setAdminComposeText,
    assistLoading,
    assistResult,
    assistError,
    saving,
    error,
    submittedProposal,
    addMessage,
    resetSandbox,
    runAssist,
    submitProposal,
    dismissDraft,
    restoreDraft,
    clearAssistResult,
    hasInvestorMessage: transcript.some((message) => message.senderRole === 'investor'),
  };
}
